import { useEffect, useId, useRef, type KeyboardEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { Lang } from '../logic/form-logic';
import type { FormCopy } from './copy';
import { FormButton } from './ui';

/**
 * "Unable to continue" (SFM-D27): an answer the pharmacy marked as one that stops an online
 * booking, with the pharmacy's own words and number.
 *
 * In the page's flow, in place of the screen's buttons, not a fixed overlay: inside the pharmacy
 * website's booking frame the frame is as tall as its content, so an overlay centred on it could
 * sit off-screen. It behaves as a modal all the same: the question behind it is inert (the
 * runner's job), focus starts on "Change my answer", Tab stays inside, and Escape means "Change
 * my answer", the one thing to do next.
 */
export function StopDialog({
  copy,
  message,
  lang,
  phone,
  preview = false,
  onChange,
  onLeave,
  leaveLabel,
}: {
  copy: FormCopy;
  /** The pharmacy's words, in the form's language. */
  message: string;
  lang: Lang;
  phone?: string | null;
  /** The builder's preview: also says what happens booking online and on a link. */
  preview?: boolean;
  onChange: () => void;
  /** A second way out, e.g. "Choose another service". */
  onLeave?: () => void;
  leaveLabel?: string;
}) {
  const ids = useId().replace(/:/g, '');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    boxRef.current?.querySelector<HTMLElement>('[data-stop-change]')?.focus();
    // Guarded: jsdom has no scrollIntoView.
    boxRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onChange();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      boxRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const tel = phone ? phone.replace(/[^\d+]/g, '') : '';
  return (
    <div
      ref={boxRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`${ids}-title`}
      aria-describedby={tel ? `${ids}-message ${ids}-call` : `${ids}-message`}
      onKeyDown={onKeyDown}
      className="rounded-xl border border-danger/40 bg-card p-5 shadow-lg"
    >
      <h2 id={`${ids}-title`} className="flex items-center gap-2 text-xl font-semibold text-foreground">
        <AlertTriangle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
        {copy.stopTitle}
      </h2>
      <p id={`${ids}-message`} lang={lang} className="mt-3 whitespace-pre-line text-lg leading-relaxed">
        {message}
      </p>
      {tel && (
        <p id={`${ids}-call`} className="mt-3 text-base">
          {copy.stopCall}{' '}
          <a href={`tel:${tel}`} className="font-medium text-primary underline underline-offset-4">
            {phone}
          </a>
        </p>
      )}
      {preview && <p className="mt-3 text-sm text-muted-foreground">{copy.stopPreview}</p>}
      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        {onLeave && leaveLabel ? (
          <FormButton variant="outline" onClick={onLeave}>
            {leaveLabel}
          </FormButton>
        ) : (
          <span />
        )}
        <FormButton data-stop-change="" onClick={onChange}>
          {copy.stopChange}
        </FormButton>
      </div>
    </div>
  );
}
